import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';

const MentionAutocomplete = ({ 
  value, 
  onChange, 
  placeholder, 
  className = '',
  rows = 3,
  disabled = false,
  onKeyDown
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);
  const textareaRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    // Handle click outside to close suggestions
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchUsers = async (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await axios.get(`${API}/users/search-usernames`, {
        params: { q: query },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSuggestions(response.data.users || []);
    } catch (error) {
      console.error('Error searching users:', error);
      setSuggestions([]);
    }
  };

  const handleTextChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    onChange(newValue);
    setCursorPosition(cursorPos);

    // Check if we're typing after an @ symbol
    const textUpToCursor = newValue.substring(0, cursorPos);
    const lastAtIndex = textUpToCursor.lastIndexOf('@');
    
    if (lastAtIndex >= 0) {
      const textAfterAt = textUpToCursor.substring(lastAtIndex + 1);
      // Check if there's no space after @ (still typing a mention)
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setMentionStart(lastAtIndex);
        setShowSuggestions(true);
        searchUsers(textAfterAt);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  const selectMention = (username) => {
    const beforeMention = value.substring(0, mentionStart);
    const afterCursor = value.substring(cursorPosition);
    const newValue = `${beforeMention}@${username} ${afterCursor}`;
    
    onChange(newValue);
    setShowSuggestions(false);
    
    // Focus back on textarea and position cursor after the mention
    setTimeout(() => {
      textareaRef.current.focus();
      const newCursorPos = beforeMention.length + username.length + 2; // +2 for @ and space
      textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        e.preventDefault();
        return;
      }
      // Suppress parent handlers (e.g. Enter-to-send) while picking a mention
      if (e.key === 'Enter') {
        return;
      }
    }
    if (onKeyDown) onKeyDown(e);
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        rows={rows}
        disabled={disabled}
      />
      
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-50 mt-1 w-64 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto"
        >
          <div className="p-2 text-xs text-gray-500 border-b">
            @mention users:
          </div>
          {suggestions.map((user) => (
            <button
              key={user.username}
              onClick={() => selectMention(user.username)}
              className="w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center gap-2"
            >
              <span className="font-medium text-blue-600">@{user.username}</span>
              {user.full_name && (
                <span className="text-gray-500 text-sm">({user.full_name})</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionAutocomplete;